//@ts-nocheck
import { call, put } from 'redux-saga/effects';
import {
  fetchActiveAlertsRequest,
  fetchActiveAlertsSuccess,
  fetchActiveAlertsFailure,
  fetchAlertContactPreferencesRequest,
  fetchAlertContactPreferencesSuccess,
  fetchAlertContactPreferencesFailure,
  updateAlertPreferencesRequest,
  updateAlertPreferencesSuccess,
  updateAlertPreferencesFailure,
  fetchPhoneOptionsRequest,
  fetchPhoneOptionsSuccess,
  fetchPhoneOptionsFailure,
  updatePhoneForAlertsRequest,
  updatePhoneForAlertsSuccess,
  updatePhoneForAlertsFailure,
  fetchEmailOptionsRequest,
  fetchEmailOptionsSuccess,
  fetchEmailOptionsFailure,
  updateEmailForAlertsRequest,
  updateEmailForAlertsSuccess,
  updateEmailForAlertsFailure,
  updateAlertContactPreferencesRequest,
  updateAlertContactPreferencesSuccess,
  updateAlertContactPreferencesFailure,
} from '../reducer/manageAlertsSlice';
import {
  getActiveAlerts,
  getAlertContactPreferences,
  updateAlertPreferences,
  fetchPhoneOptions,
  updatePhoneForAlerts,
  fetchEmailOptions,
  updateEmailForAlerts,
  updateAlertContactPreferences,
} from '../api';
import { isWeb } from '../storage';

interface FetchActiveAlertsAction {
  type: typeof fetchActiveAlertsRequest.type;
  payload: { accountId: string; customerId: string };
}

interface FetchAlertContactPreferencesAction {
  type: typeof fetchAlertContactPreferencesRequest.type;
  payload: { accountId: string; customerId: string };
}

// New interface for update action
interface UpdateAlertPreferencesAction {
  type: typeof updateAlertPreferencesRequest.type;
  payload: {
    accountId: string;
    customerId: string;
    userNotificationPreferences: any[];
    recaptchaToken?:string|null|undefined;
  };
}

// New interfaces for phone options and update actions
interface FetchPhoneOptionsAction {
  type: typeof fetchPhoneOptionsRequest.type;
  payload: { accountId: string; customerId: string };
}

interface UpdatePhoneForAlertsAction {
  type: typeof updatePhoneForAlertsRequest.type;
  payload: {
    accountId: string;
    customerId: string;
    requestBody: any;
  };
}

// New interfaces for email options and update actions
interface FetchEmailOptionsAction {
  type: typeof fetchEmailOptionsRequest.type;
  payload: { accountId: string; customerId: string };
}

interface UpdateEmailForAlertsAction {
  type: typeof updateEmailForAlertsRequest.type;
  payload: {
    accountId: string;
    customerId: string;
    requestBody: any;
  };
}

// New interface for updating alert contact preferences action
interface UpdateAlertContactPreferencesAction {
  type: typeof updateAlertContactPreferencesRequest.type;
  payload: {
    requestBody: any;
  };
}

// Add utility function to parse and format error messages
function formatApiError(error: any): string {
  // Check if the error already contains a structured message
  if (error.response && error.response.data) {
    try {
      // If it's a JSON string, parse it
      if (
        typeof error.response.data === 'string' &&
        error.response.data.startsWith('{')
      ) {
        return error.response.data;
      }
      // If it's already an object, stringify it
      else if (typeof error.response.data === 'object') {
        return JSON.stringify(error.response.data);
      }
    } catch (e) {
      // If parsing fails, fallback to the error message
    }
  }

  // Try to extract message from error object
  const message = error.message || 'An unknown error occurred';
  const status = error.status || error.statusCode || '500';
  const detail = error.detail || message;
  const title = error.title || 'Error';

  // Return formatted JSON error
  return JSON.stringify({
    status,
    detail,
    title,
  });
}

// Fetch active alerts saga
export function* fetchActiveAlertsSaga(
  action: FetchActiveAlertsAction
): Generator<any, void, unknown> {
  try {
    const { accountId, customerId } = action.payload;
    console.log('ManageAlertsSaga: Fetching alerts for', accountId, customerId);

    // Use v2 endpoint for both web and mobile
    const apiEndpoint = 'fetch_active_alerts_v2';
    
    const alertsResponse: any = yield call(
      getActiveAlerts,
      apiEndpoint,
      accountId,
      customerId
    );

    console.log(
      'ManageAlertsSaga: Received response',
      alertsResponse ? 'with data' : 'without data',
      alertsResponse?.alerts
        ? `(${alertsResponse.alerts.length} alerts)`
        : '(no alerts array)'
    );

    if (alertsResponse) {
      // Ensure the response has the expected structure
      const normalizedResponse = {
        alerts: alertsResponse.alerts || [],
        contactPrefs: alertsResponse.contactPrefs || null,
      };

      yield put(fetchActiveAlertsSuccess(normalizedResponse));
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error fetching alerts',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(fetchActiveAlertsFailure(formattedError));
  }
}

// Fetch alert contact preferences saga
export function* fetchAlertContactPreferencesSaga(
  action: FetchAlertContactPreferencesAction
): Generator<any, void, unknown> {
  try {
    const { accountId, customerId } = action.payload;
    console.log(
      'ManageAlertsSaga: Fetching alert contact preferences for',
      accountId,
      customerId
    );

    const preferencesResponse: any = yield call(
      getAlertContactPreferences,
      'fetch_alert_contact_preferences',
      accountId,
      customerId
    );

    console.log(
      'ManageAlertsSaga: Received alert contact preferences response',
      preferencesResponse ? 'with data' : 'without data'
    );

    if (preferencesResponse) {
      yield put(fetchAlertContactPreferencesSuccess(preferencesResponse));
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error fetching alert contact preferences',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(fetchAlertContactPreferencesFailure(formattedError));
  }
}

// Update alert preferences saga
export function* updateAlertPreferencesSaga(
  action: UpdateAlertPreferencesAction
): Generator<any, void, unknown> {
  try {
    const { accountId, customerId, userNotificationPreferences, recaptchaToken} =
      action.payload;
    console.log(
      'ManageAlertsSaga: Updating alert preferences for',
      accountId,
      customerId
    );

    const updateResponse: any = yield call(
      updateAlertPreferences,
      'update_alert_preferences',
      accountId,
      customerId,
      userNotificationPreferences,
      recaptchaToken
    );

    console.log(
      'ManageAlertsSaga: Received update response',
      updateResponse ? 'with data' : 'without data'
    );

    // Consider any non-error response as success
    yield put(updateAlertPreferencesSuccess(updateResponse || {}));

    // Fetch updated alerts to refresh the UI
    yield put(
      fetchActiveAlertsRequest({
        accountId,
        customerId,
      })
    );
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error updating alert preferences',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(updateAlertPreferencesFailure(formattedError));
  }
}

// Fetch phone options saga
export function* fetchPhoneOptionsSaga(
  action: FetchPhoneOptionsAction
): Generator<any, void, unknown> {
  try {
    const { accountId, customerId } = action.payload;
    console.log(
      'ManageAlertsSaga: Fetching phone options for',
      accountId,
      customerId
    );

    const phoneOptionsResponse: any = yield call(
      fetchPhoneOptions,
      'phone_options',
      accountId,
      customerId
    );

    console.log(
      'ManageAlertsSaga: Received phone options response',
      phoneOptionsResponse ? 'with data' : 'without data'
    );

    if (phoneOptionsResponse) {
      yield put(fetchPhoneOptionsSuccess(phoneOptionsResponse));
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error fetching phone options',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(fetchPhoneOptionsFailure(formattedError));
  }
}

// Update phone for alerts saga
export function* updatePhoneForAlertsSaga(
  action: UpdatePhoneForAlertsAction
): Generator<any, void, unknown> {
  try {
    const { accountId, customerId, requestBody } = action.payload;
    console.log(
      'ManageAlertsSaga: Updating phone for alerts',
      accountId,
      customerId
    );

    const updateResponse: any = yield call(
      updatePhoneForAlerts,
      'update_phone',
      accountId,
      customerId,
      requestBody
    );

    console.log(
      'ManageAlertsSaga: Received update phone response',
      updateResponse ? 'with data' : 'without data'
    );

    if (updateResponse) {
      yield put(updatePhoneForAlertsSuccess(updateResponse));

      // Refresh alert contact preferences after updating phone
      yield put(
        fetchAlertContactPreferencesRequest({
          accountId,
          customerId,
        })
      );
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error updating phone for alerts',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(updatePhoneForAlertsFailure(formattedError));
  }
}

// Fetch email options saga
export function* fetchEmailOptionsSaga(
  action: FetchEmailOptionsAction
): Generator<any, void, unknown> {
  try {
    const { accountId, customerId } = action.payload;
    console.log(
      'ManageAlertsSaga: Fetching email options for',
      accountId,
      customerId
    );

    const emailOptionsResponse: any = yield call(
      fetchEmailOptions,
      'email_options',
      accountId,
      customerId
    );

    console.log(
      'ManageAlertsSaga: Received email options response',
      emailOptionsResponse ? 'with data' : 'without data'
    );

    if (emailOptionsResponse) {
      yield put(fetchEmailOptionsSuccess(emailOptionsResponse));
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error fetching email options',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(fetchEmailOptionsFailure(formattedError));
  }
}

// Update email for alerts saga
export function* updateEmailForAlertsSaga(
  action: UpdateEmailForAlertsAction
): Generator<any, void, unknown> {
  try {
    const { accountId, customerId, requestBody } = action.payload;
    console.log(
      'ManageAlertsSaga: Updating email for alerts',
      accountId,
      customerId
    );

    const updateResponse: any = yield call(
      updateEmailForAlerts,
      'update_email',
      accountId,
      customerId,
      requestBody
    );

    console.log(
      'ManageAlertsSaga: Received update email response',
      updateResponse ? 'with data' : 'without data'
    );

    if (updateResponse) {
      yield put(updateEmailForAlertsSuccess(updateResponse));

      // Don't refresh alert contact preferences after updating email
      // to avoid multiple success messages
      // yield put(fetchAlertContactPreferencesRequest({
      //   accountId,
      //   customerId
      // }));
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error updating email for alerts',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(updateEmailForAlertsFailure(formattedError));
  }
}

// Update alert contact preferences saga
export function* updateAlertContactPreferencesSaga(
  action: UpdateAlertContactPreferencesAction
): Generator<any, void, unknown> {
  try {
    const { requestBody } = action.payload;
    console.log(
      'ManageAlertsSaga: Updating alert contact preferences with data:',
      JSON.stringify(requestBody, null, 2)
    );

    const updateResponse: any = yield call(
      updateAlertContactPreferences,
      'update_alert_contact_preferences',
      requestBody
    );

    console.log(
      'ManageAlertsSaga: Received alert contact preferences update response:',
      updateResponse ? JSON.stringify(updateResponse, null, 2) : 'without data'
    );

    if (updateResponse) {
      yield put(updateAlertContactPreferencesSuccess(updateResponse));
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error: any) {
    console.error(
      'ManageAlertsSaga: Error updating alert contact preferences',
      error.message || error
    );

    // Use the formatting function for consistent error structure
    const formattedError = formatApiError(error);

    yield put(updateAlertContactPreferencesFailure(formattedError));
  }
}
